"use client";

import { Check } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { useSearchAirportQuery } from "@/lib/api/common-api";
import { cn } from "@/lib/utils";

import { TAirport } from "@/feature/flight/search/types/search";

interface AirportSelectProps {
  value?: TAirport;
  onChange: (value: TAirport) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  isError?: boolean;
}

function normalizeCoordinate(value: unknown): number | null | undefined {
  if (value === undefined || value === "") {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (trimmed === "") {
      return undefined;
    }

    if (trimmed.toLowerCase() === "null") {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function normalizeAirport(airport: TAirport): TAirport {
  const rawAirport = airport as TAirport & {
    latitude?: number | string | null;
    longitude?: number | string | null;
  };

  return {
    ...airport,
    latitude: normalizeCoordinate(rawAirport.latitude),
    longitude: normalizeCoordinate(rawAirport.longitude),
  };
}

export function formatAirportDisplay(airport: TAirport): string {
  const parts = [airport.iata_code];

  if (airport.city_name) {
    parts.push(airport.city_name);
  }

  // if (airport.name) {
  //   parts.push(`(${airport.name})`);
  // }

  return parts.join(" - ");
}

export function AirportSelect({
  value,
  onChange,
  label,
  placeholder = "Select airport",
  disabled,
  isError = false,
}: AirportSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useSearchAirportQuery(
    {
      name: debouncedSearchTerm,
      skip: 0,
      limit: 40,
    },
    {
      refetchOnReconnect: false,
      refetchOnMountOrArgChange: true,
    },
  );

  const airports = React.useMemo(
    () =>
      (data?.data ?? []).map((airport) =>
        normalizeAirport(airport as TAirport),
      ),
    [data?.data],
  );

  // Find selected airport from all airports, not just search results
  const selectedAirport = React.useMemo(() => {
    if (!value || !value.iata_code || value.iata_code.trim() === "")
      return null;
    // Value is already the full airport object
    return value;
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          variant={"ghost"}
          aria-expanded={open}
          aria-invalid={isError}
          aria-label={
            selectedAirport
              ? `${label ?? "Airport"}: ${selectedAirport.iata_code} ${selectedAirport.city_name || selectedAirport.name || ""}`.trim()
              : label ?? placeholder
          }
          className={cn(
            "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
            !selectedAirport && "text-muted-foreground",
            isError && "border-destructive",
          )}
          disabled={disabled}
        >
          {selectedAirport ? (
            <div className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
              <span className="text-base font-bold tracking-tight text-foreground sm:text-[1.05rem]">
                {selectedAirport.iata_code}
              </span>
              <span className="h-9 w-px bg-border" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground/85">
                  {selectedAirport.city_name || selectedAirport.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[selectedAirport.country, selectedAirport.name]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </span>
            </div>
          ) : (
            <span className="min-w-0 text-left">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {label}
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {placeholder}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-90 max-w-[calc(100vw-2rem)] p-0 h-48 sm:h-full"
        align="start"
      >
        <Command shouldFilter={false} className="relative">
          <CommandInput
            placeholder="Search by city, airport, or code..."
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading || isFetching ? "Loading..." : "No results found"}
            </CommandEmpty>
            {airports.length > 0 && (
              <CommandGroup>
                {airports.map((airport, i) => (
                  <CommandItem
                    key={airport.iata_code + i}
                    value={airport.iata_code}
                    onSelect={() => {
                      onChange(airport);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.iata_code === airport.iata_code &&
                          value.iata_code !== ""
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {/* <Plane className="mr-2 h-4 w-4 text-muted-foreground" /> */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {airport.iata_code}
                        </span>
                        <span>{airport.city_name}</span>
                      </div>
                      {airport.name && (
                        <span className="text-xs text-muted-foreground line-clamp-1">
                          {airport.name}
                          {airport.country && `, ${airport.country}`}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
